<template>
  <div
    class="w-100 d-flex flex-column ga-6"
    :class="{
      'bg-white rounded-xl': !embedded,
      'pa-3': !embedded && mobile,
      'pa-5': !embedded && !mobile,
    }"
    :style="!embedded && !mobile ? { maxWidth: '364px' } : undefined"
  >
    <div
      v-if="hasBanner"
      class="d-flex align-center justify-center rounded-xl bg-primary-30"
      :style="{ height: mobile ? '180px' : '250px' }"
    >
      <img
        :src="calendarImage"
        alt="یادآوری"
        :style="{
          width: mobile ? '140px' : '186px',
          height: mobile ? '125px' : '166px',
          objectFit: 'contain',
        }"
      />
    </div>

    <div class="d-flex flex-column ga-1">
      <div class="d-flex align-center justify-space-between mb-1">
        <button
          type="button"
          class="d-inline-flex align-center justify-center rounded-circle border-0 bg-transparent cursor-pointer"
          style="width: 36px; height: 36px"
          aria-label="ماه قبل"
          @click="goPrevMonth"
        >
          <VIcon icon="chevronRight" size="18" color="gray-600" />
        </button>

        <span class="font-weight-medium text-gray-700">
          {{ monthTitle }}
        </span>

        <button
          type="button"
          class="d-inline-flex align-center justify-center rounded-circle border-0 bg-transparent cursor-pointer"
          style="width: 36px; height: 36px"
          aria-label="ماه بعد"
          @click="goNextMonth"
        >
          <VIcon icon="chevronLeft" size="18" color="gray-600" />
        </button>
      </div>

      <div class="d-flex justify-space-between">
        <div
          v-for="label in WEEKDAY_LABELS"
          :key="label"
          class="d-inline-flex flex-column align-center justify-center position-relative rounded-circle pa-2 font-weight-medium text-gray-500"
          style="width: 40px; min-height: 40px"
        >
          {{ label }}
        </div>
      </div>

      <div
        v-for="(week, weekIndex) in weeks"
        :key="weekIndex"
        class="d-flex justify-space-between"
      >
        <button
          v-for="(cell, dayIndex) in week"
          :key="`${weekIndex}-${dayIndex}`"
          type="button"
          class="reminder-calendar__day d-inline-flex flex-column align-center justify-center position-relative rounded-circle pa-2 font-weight-medium"
          style="width: 40px; min-height: 40px; font-size: 16px"
          :class="{
            'reminder-calendar__day--empty': cell.day === null,
            'reminder-calendar__day--friday': cell.isFriday,
            'reminder-calendar__day--today': cell.isToday,
            'reminder-calendar__day--selected':
              cell.dateKey !== null && cell.dateKey === selectedDateKey,
          }"
          :disabled="cell.day === null"
          @click="onSelectDay(cell.dateKey)"
        >
          <span v-if="cell.day !== null">
            {{ toPersianDigits(cell.day) }}
          </span>
          <span
            v-if="cell.dateKey && markedDateKeys.has(cell.dateKey)"
            class="reminder-calendar__dot position-absolute"
          />
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue'
import { VIcon } from 'vuetify/components'
import type { Emits, Props } from './ReminderCalendar.types'
import calendarImage from '@/assets/images/calendar-clock-3d.png'
import {
  WEEKDAY_LABELS,
  buildMonthGrid,
  formatMonthTitle,
  getJalaliParts,
  shiftJalaliMonth,
  toPersianDigits,
} from '@/modules/Reminder/utils/jalaliCalendar'

defineOptions({
  name: 'ReminderCalendar',
})

const props = withDefaults(defineProps<Props>(), {
  mobile: false,
  embedded: false,
})

const emit = defineEmits<Emits>()

const todayParts = getJalaliParts()
const viewYear = ref(todayParts.year)
const viewMonth = ref(todayParts.month)

const monthTitle = computed(() =>
  formatMonthTitle(viewYear.value, viewMonth.value)
)

const weeks = computed(() => {
  const cells = buildMonthGrid(viewYear.value, viewMonth.value)
  const rows: (typeof cells)[] = []
  for (let i = 0; i < cells.length; i += 7) {
    rows.push(cells.slice(i, i + 7))
  }
  return rows
})

function goPrevMonth() {
  const next = shiftJalaliMonth(viewYear.value, viewMonth.value, -1)
  viewYear.value = next.year
  viewMonth.value = next.month
}

function goNextMonth() {
  const next = shiftJalaliMonth(viewYear.value, viewMonth.value, 1)
  viewYear.value = next.year
  viewMonth.value = next.month
}

watch([viewYear, viewMonth], ([year, month]) => {
  emit('update:viewMonth', { year, month })
})

function onSelectDay(dateKey: string | null) {
  if (!dateKey) {
    return
  }
  emit(
    'update:selectedDateKey',
    props.selectedDateKey === dateKey ? null : dateKey
  )
}
</script>

<style scoped lang="scss">
.reminder-calendar__day {
  border: 1px solid transparent;
  background: transparent;
  color: rgb(var(--v-theme-gray-700));
  cursor: pointer;

  &--empty {
    cursor: default;
    pointer-events: none;
  }

  &--friday {
    color: rgb(var(--v-theme-error));
  }

  &--today {
    background: #fff0de;
    border-color: rgb(var(--v-theme-warning));
  }

  &--selected:not(.reminder-calendar__day--today) {
    background: rgba(var(--v-theme-primary), 0.08);
    border-color: rgb(var(--v-theme-primary));
  }
}

.reminder-calendar__dot {
  bottom: 4px;
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #22c55e;
}
</style>
